/* Restock pot on the Inventory page — filled by Quick $15 fees, releases when it covers the full restock total. */
import { useCallback, useEffect, useState } from "react";
import { api, fmtMoney, formatErr } from "@/lib/api";
import { PiggyBank, Lock, Unlock, Loader2 } from "lucide-react";
import RestockOrderPanel from "@/components/RestockOrderPanel";
import RestockPotSchedule from "@/components/RestockPotSchedule";

export default function RestockPot({ restockTotal, onInventoryChanged }) {
  const [pot, setPot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [released, setReleased] = useState(false);
  const load = useCallback(() => api.get("/restock-pot").then((r) => setPot(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load, restockTotal]);
  if (!pot) return null;

  const release = async () => {
    setBusy(true); setMsg("");
    try { const { data } = await api.post("/restock-pot/release"); setMsg(`${fmtMoney(data.released)} released to Spending — go restock.`); setPot(data.pot); setReleased(true); }
    catch (e) { setMsg(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-8 bg-ink text-cream rounded-2xl p-6 flex items-center justify-between gap-6 flex-wrap" data-testid="restock-pot">
      <div className="flex items-center gap-4">
        <div className="w-11 h-11 rounded-full bg-gold/20 text-gold inline-flex items-center justify-center"><PiggyBank className="w-5 h-5" strokeWidth={1.75} /></div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-gold">Restock pot · savings</div>
          <div className="font-mono text-3xl mt-0.5" data-testid="restock-pot-balance">{fmtMoney(pot.balance)}</div>
          <div className="text-[11px] text-cream/70 mt-0.5">Every Quick $15 pull drops a $15 fee in here. {pot.restock_total > 0 ? `Unlocks once it covers your ${fmtMoney(pot.restock_total)} restock.` : "Add inventory items to set its target."}</div>
        </div>
      </div>
      <div className="flex-1 min-w-[180px] max-w-xs">
        <div className="h-2 rounded-full bg-cream/15 overflow-hidden"><div className="h-full bg-gold" style={{ width: `${pot.progress}%` }} data-testid="restock-pot-progress" /></div>
        <div className="mt-1 text-[10px] font-mono text-cream/70 text-right">{pot.progress.toFixed(0)}% of restock total</div>
      </div>
      <button onClick={release} disabled={busy || !pot.can_release}
        className="inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-xs font-medium bg-gold text-ink hover:bg-gold-deep disabled:opacity-40 disabled:cursor-not-allowed" data-testid="restock-pot-release">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : pot.can_release ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
        {pot.can_release ? "Release to Spending" : "Sealed until it covers restock"}
      </button>
      {msg && <div className="w-full text-xs text-gold" data-testid="restock-pot-msg">{msg}</div>}
      <RestockPotSchedule pot={pot} onChanged={setPot} />
      {(pot.can_release || released) && <RestockOrderPanel onRestocked={(d) => { onInventoryChanged?.(d); load(); }} />}
    </div>
  );
}
